import { useRef, useEffect, useState } from 'react';

import useTrackSelectParentSize from './use-track-select-parent-size';
import createScheduler from '@/polyfills/create-scheduler';

const useSetMenuPosition = ({
  controlRef,
  menuRef,
  pause,
}: {
  pause: boolean;
  controlRef: React.RefObject<HTMLElement | null>;
  menuRef: React.RefObject<HTMLElement | null>;
}) => {
  const selectRefParentRef = useRef<HTMLElement | null>(null);
  const hasTracked = useRef(false);

  const [optimisticallyCalculated, setOptimisticallyCalculated] = useState(false);

  useEffect(() => {
    selectRefParentRef.current = controlRef.current?.parentElement || null;
  }, [controlRef]);

  useEffect(() => {
    if (!selectRefParentRef.current) return;

    if (hasTracked.current) {
      setOptimisticallyCalculated(false); // reset to false to allow pause to work as expected
      return;
    }

    // optimistic calculation to prevent initial brief flash first time we open the menu due to fixed positioning
    // starting at top: 0 left: 0
    const taskScheduler = createScheduler();
    taskScheduler.schedule(
      () => {
        setOptimisticallyCalculated(true);
        hasTracked.current = true;
      },
      {
        delay: 0,
        priority: 'background',
      }
    );

    return () => {
      taskScheduler.yield();
    };
  }, []);

  const boundingRect = useTrackSelectParentSize({
    ref: selectRefParentRef,
    pause: pause && !optimisticallyCalculated,
  });

  useEffect(() => {
    if (pause && !optimisticallyCalculated) return;
    if (!menuRef.current) return;
    if (!selectRefParentRef.current) return;

    menuRef.current.style.setProperty('--select-menu-top', `${boundingRect.bottom + 4}px`);
    menuRef.current.style.setProperty('--select-menu-left', `${boundingRect.left}px`);
    menuRef.current.style.setProperty('--select-menu-width', `${boundingRect.width}px`);
  }, [boundingRect, pause, menuRef, optimisticallyCalculated]);
};

export default useSetMenuPosition;
